import { useRef, useEffect, useState, useCallback } from 'react';
import { getFormattedDateTimeLocal } from 'utils/getFormattedDate';
import { Box, Typography, Stack, Fade, Chip, Tooltip } from '@mui/material';
import CustomTooltip from 'components/CustomTooltip';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloseOutlined, RobotOutlined, DeleteOutlined,
  SearchOutlined, HistoryOutlined, PlusOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import IconButton from 'components/@extended/IconButton';
import { UseAiChatReturn, ChatSession } from '../../hooks/useAiChat';
import ChatCardRouter from './ChatCardRouter';
import AiChatInput from './AiChatInput';
import TurnContainer, { groupCardsByTurn } from './TurnContainer';

interface AiChatPopupProps {
  open: boolean;
  onClose: () => void;
  chat: UseAiChatReturn;
}

const MIN_WIDTH = 420;
const MIN_HEIGHT = 520;
const DEFAULT_WIDTH = 520;
const DEFAULT_HEIGHT = 720;

const ANALYZE_REPORT_PREFILL = 'Analyze report for application <APP_NUMBER> owner <OWNER>';

type ResizeEdge = 'top' | 'left' | 'top-left' | null;
type View = 'chat' | 'history';

const QuickActionCard = ({ icon, title, desc, onClick }: { icon: React.ReactNode; title: string; desc: string; onClick: () => void }) => {
  const theme = useTheme();
  return (
    <Box onClick={onClick} sx={{
      p: 1.75, borderRadius: 2, cursor: 'pointer',
      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
      bgcolor: alpha(theme.palette.background.paper, 0.4),
      transition: 'all 0.18s ease',
      '&:hover': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          fontSize: 16,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.35, mb: 0.5, color: 'text.primary' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.5 }}>
            {desc}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const SessionListItem = ({ session, onLoad, onDelete }: { session: ChatSession; onLoad: () => void; onDelete: () => void }) => {
  const theme = useTheme();
  const timeStr = getFormattedDateTimeLocal(session.updatedAt);
  const title = session.title || 'New Chat';

  return (
    <Box onClick={onLoad} sx={{
      px: 2, py: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
      transition: 'background-color 0.12s ease',
      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      '&:hover .delete-btn': { opacity: 1 },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CustomTooltip title={title} placement="top-start">
          <Typography sx={{
            fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.35, mb: 0.25,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            color: 'text.primary',
          }}>
            {title}
          </Typography>
        </CustomTooltip>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.3 }}>
          {timeStr}
        </Typography>
      </Box>
      <IconButton className="delete-btn" size="small" sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}
        onClick={(e) => { e.stopPropagation(); onDelete(); }}>
        <DeleteOutlined style={{ fontSize: 14, color: theme.palette.error.main }} />
      </IconButton>
    </Box>
  );
};

const HeaderIconButton = ({ children, onClick, tooltip }: { children: React.ReactNode; onClick: () => void; tooltip?: string }) => {
  const button = (
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 32, height: 32,
        color: alpha('#fff', 0.75),
        transition: 'all 0.15s ease',
        '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.14) },
      }}
    >
      {children}
    </IconButton>
  );
  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

const AiChatPopup = ({ open, onClose, chat }: AiChatPopupProps) => {
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    cards, sessions, isConnected, agentOnline, turnState,
    cancelResponse, clearMessages,
    loadSessions, loadSession, deleteSession, newSession,
    sendMessage, pickOwner, dismissNoReport, retryNoReport,
    dismissErrorCard, retryCommand, endTurn, retry,
  } = chat;

  const connectivity: 'connected' | 'agent_offline' | 'disconnected' = !isConnected
    ? 'disconnected'
    : !agentOnline
      ? 'agent_offline'
      : 'connected';

  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [prefillText, setPrefillText] = useState('');
  const [view, setView] = useState<View>('chat');
  const resizeRef = useRef<{ edge: ResizeEdge; startX: number; startY: number; startW: number; startH: number } | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [cards]);

  useEffect(() => {
    const clampToViewport = () => {
      setSize(prev => {
        const maxW = Math.max(MIN_WIDTH, window.innerWidth - 48);
        const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 80);
        const nextW = Math.min(prev.width, maxW);
        const nextH = Math.min(prev.height, maxH);
        if (nextW === prev.width && nextH === prev.height) return prev;
        return { width: nextW, height: nextH };
      });
    };
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, []);

  const handleResizeStart = useCallback((edge: ResizeEdge) => (e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { edge, startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const { edge, startX, startY, startW, startH } = resizeRef.current;
      let newW = startW, newH = startH;
      if (edge === 'left' || edge === 'top-left') newW = Math.max(MIN_WIDTH, startW - (ev.clientX - startX));
      if (edge === 'top' || edge === 'top-left') newH = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - 80, startH - (ev.clientY - startY)));
      setSize({ width: newW, height: newH });
    };
    const handleMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = edge === 'top-left' ? 'nw-resize' : edge === 'top' ? 'n-resize' : 'w-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [size]);

  const handleQuickAction = useCallback((text: string) => { setPrefillText(text); }, []);

  const handleSend = useCallback((text: string) => {
    setPrefillText('');
    sendMessage(text);
    if (view === 'history') setView('chat');
  }, [sendMessage, view]);

  const openHistory = useCallback(() => {
    loadSessions();
    setView('history');
  }, [loadSessions]);

  const handleLoadSession = useCallback((sessionId: string) => {
    loadSession(sessionId);
    setView('chat');
  }, [loadSession]);

  const handleNewSession = useCallback(() => {
    newSession();
    setView('chat');
  }, [newSession]);

  const lastUserMessage = [...cards].reverse().find(c => c.type === 'user_message');
  const lastUserText = lastUserMessage?.type === 'user_message' ? lastUserMessage.text : '';

  if (!open) return null;

  const chatAreaBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.6)
    : theme.palette.background.default;

  return (
    <Fade in={open} timeout={220}>
      <Box ref={containerRef} sx={{
        position: 'fixed', bottom: 24, right: 24,
        width: size.width, height: size.height, maxHeight: 'calc(100vh - 80px)',
        display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden',
        zIndex: theme.zIndex.modal + 1,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        boxShadow: theme.palette.mode === 'dark'
          ? `0 24px 70px ${alpha(theme.palette.common.black, 0.6)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.3)}`
          : `0 24px 70px ${alpha(theme.palette.common.black, 0.22)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
      }}>
        <Box onMouseDown={handleResizeStart('top')} sx={{ position: 'absolute', top: 0, left: 14, right: 14, height: 6, cursor: 'n-resize', zIndex: 10 }} />
        <Box onMouseDown={handleResizeStart('left')} sx={{ position: 'absolute', top: 14, left: 0, bottom: 14, width: 6, cursor: 'w-resize', zIndex: 10 }} />
        <Box onMouseDown={handleResizeStart('top-left')} sx={{ position: 'absolute', top: 0, left: 0, width: 16, height: 16, cursor: 'nw-resize', zIndex: 11 }} />

        <Box sx={{
          px: 2.25, py: 1.75, flexShrink: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha('#000', 0.15)}`,
        }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            {view === 'history' ? (
              <HeaderIconButton onClick={() => setView('chat')} tooltip="Back">
                <ArrowLeftOutlined style={{ fontSize: 16 }} />
              </HeaderIconButton>
            ) : (
              <Box sx={{
                width: 38, height: 38, borderRadius: 1.75, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha('#fff', 0.18),
                border: `1px solid ${alpha('#fff', 0.2)}`,
                boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.15)}`,
              }}>
                <RobotOutlined style={{ fontSize: 19, color: '#fff' }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                color: '#fff', fontWeight: 700, lineHeight: 1.2,
                fontSize: '1rem', letterSpacing: -0.1,
              }}>
                {view === 'history' ? 'Chat History' : 'AI Assistant'}
              </Typography>
              {view === 'chat' && (
                <Typography sx={{
                  color: alpha('#fff', 0.72),
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: 0.2,
                  mt: 0.25,
                }}>
                  Application · Underwriting
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {(() => {
              const dot = connectivity === 'connected' ? '#4ade80' : connectivity === 'agent_offline' ? '#fbbf24' : '#f87171';
              const text = connectivity === 'connected' ? '#86efac' : connectivity === 'agent_offline' ? '#fde68a' : '#fca5a5';
              const label = connectivity === 'connected' ? 'Connected' : connectivity === 'agent_offline' ? 'Agent offline' : 'Offline';
              return (
                <Chip
                  size="small"
                  icon={
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%',
                      bgcolor: dot,
                      boxShadow: connectivity === 'connected' ? `0 0 8px ${alpha('#4ade80', 0.8)}` : 'none',
                      ml: '10px !important',
                    }} />
                  }
                  label={label}
                  sx={{
                    height: 24, mr: 0.5,
                    fontSize: '0.75rem', fontWeight: 600, letterSpacing: 0.2,
                    bgcolor: alpha('#000', 0.25),
                    color: text,
                    border: `1px solid ${alpha('#fff', 0.12)}`,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              );
            })()}
            {view === 'chat' && (
              <>
                <HeaderIconButton onClick={openHistory} tooltip="Chat History">
                  <HistoryOutlined style={{ fontSize: 15 }} />
                </HeaderIconButton>
                <HeaderIconButton onClick={handleNewSession} tooltip="New Chat">
                  <PlusOutlined style={{ fontSize: 15 }} />
                </HeaderIconButton>
                {cards.length > 0 && (
                  <HeaderIconButton onClick={clearMessages} tooltip="Delete Chat">
                    <DeleteOutlined style={{ fontSize: 15 }} />
                  </HeaderIconButton>
                )}
              </>
            )}
            <HeaderIconButton onClick={onClose} tooltip="Close">
              <CloseOutlined style={{ fontSize: 15 }} />
            </HeaderIconButton>
          </Stack>
        </Box>

        {view === 'chat' && connectivity === 'agent_offline' && (
          <Box sx={{
            px: 2.25, py: 1.25,
            bgcolor: alpha(theme.palette.warning.main, 0.12),
            borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            display: 'flex', alignItems: 'flex-start', gap: 1.25,
          }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: theme.palette.warning.main,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.25)}`,
              mt: 0.875, flexShrink: 0,
            }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 700,
                color: theme.palette.warning.main,
                lineHeight: 1.3, mb: 0.25,
                letterSpacing: 0.2,
              }}>
                AI agent is unreachable right now
              </Typography>
              <Typography sx={{
                fontSize: '0.78rem',
                color: alpha(theme.palette.text.primary, 0.78),
                lineHeight: 1.45,
              }}>
                Your last request couldn't be delivered. The agent may be offline or restarting — retries will resume automatically as soon as it responds.
              </Typography>
            </Box>
          </Box>
        )}

        {view === 'history' && (
          <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: chatAreaBg }}>
            {sessions.length === 0 ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(theme.palette.text.disabled, 0.1),
                }}>
                  <HistoryOutlined style={{ fontSize: 22, color: theme.palette.text.disabled }} />
                </Box>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', fontWeight: 500 }}>
                  No chat history yet
                </Typography>
                <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>
                  Your past conversations will appear here
                </Typography>
              </Box>
            ) : (
              sessions.map(session => (
                <SessionListItem
                  key={session.id}
                  session={session}
                  onLoad={() => handleLoadSession(session.id)}
                  onDelete={() => deleteSession(session.id)}
                />
              ))
            )}
          </Box>
        )}

        {view === 'chat' && (
          <>
            <Box sx={{
              flex: 1, overflowY: 'auto', px: 2.5, py: 2.25,
              bgcolor: chatAreaBg,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.text.disabled, 0.25),
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.text.disabled, 0.4),
              },
            }}>
              {cards.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                    <Box sx={{
                      position: 'relative',
                      width: 64, height: 64, borderRadius: 2.5, mx: 'auto', mb: 2.25,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.4)}, inset 0 1px 0 ${alpha('#fff', 0.25)}`,
                      '&::after': {
                        content: '""', position: 'absolute', inset: -6, borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                    }}>
                      <RobotOutlined style={{ fontSize: 30, color: '#fff' }} />
                    </Box>
                    <Typography sx={{
                      fontSize: '1.25rem', fontWeight: 700, mb: 0.75,
                      letterSpacing: -0.3, color: 'text.primary',
                    }}>
                      AI Underwriting
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.9375rem', color: 'text.secondary',
                      maxWidth: 340, mx: 'auto', lineHeight: 1.55,
                    }}>
                      The AI Assistant will analyze reports, assess risks, and make underwriting recommendations. The final decision should remain with a human.
                    </Typography>
                  </Box>

                  <Typography sx={{
                    fontSize: '0.6875rem', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 1.5,
                    color: 'text.disabled', mb: 1.25, pl: 0.5,
                  }}>
                    Quick start
                  </Typography>

                  <Stack spacing={1.25}>
                    <QuickActionCard
                      icon={<SearchOutlined />}
                      title="Analyze Owner"
                      desc="Prefills command — replace <APP_NUMBER> and <OWNER>, then send"
                      onClick={() => handleQuickAction(ANALYZE_REPORT_PREFILL)}
                    />
                  </Stack>

                  <Box sx={{
                    mt: 3, pt: 2.25,
                    borderTop: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
                    textAlign: 'center',
                  }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>
                      Type{' '}
                      <Box component="span" sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        minWidth: 20, height: 20, px: 0.75, mx: 0.25,
                        borderRadius: 0.75,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        fontFamily: 'SF Mono, Menlo, Consolas, monospace',
                        fontSize: '0.75rem', fontWeight: 700,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      }}>
                        /
                      </Box>
                      {' '}in the input for quick commands
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  {(() => {
                    const groups = groupCardsByTurn(cards);
                    const lastGroupIdx = groups.length - 1;
                    return groups.map((group, groupIdx) => {
                      const lastCardId = group[group.length - 1].id;
                      return (
                        <TurnContainer key={group[0].id + '-turn'} cards={group}>
                          {group.map((card) => (
                            <ChatCardRouter
                              key={card.id}
                              card={card.type === 'agent_error' ? { ...card, lastUserMessage: card.lastUserMessage ?? lastUserText } : card}
                              isLast={groupIdx === lastGroupIdx && card.id === lastCardId}
                              onDismissNoReport={dismissNoReport}
                              onRetryNoReport={retryNoReport}
                              onPickOwner={pickOwner}
                              onDismissError={dismissErrorCard}
                              onRetryCommand={retryCommand}
                              onRetry={retry}
                            />
                          ))}
                        </TurnContainer>
                      );
                    });
                  })()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>
            <AiChatInput
              onSend={handleSend}
              onCancel={cancelResponse}
              onEndTurn={endTurn}
              turnState={turnState}
              disabled={!isConnected}
              prefillText={prefillText}
              onPrefillConsumed={() => setPrefillText('')}
            />
          </>
        )}
      </Box>
    </Fade>
  );
};

export default AiChatPopup;
